import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../api';
import { taskTemplateSchema } from '../../schemas';
import { toast } from '../../utils/toast';
import { ModernBadge, ModernButton, ModernCard, ModernInput } from '../ui/modern';
import Modal from '../ui/Modal';
import IconPicker from '../ui/IconPicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';

const GROUPS = [
  { value: 'internalSystems', label: 'Internal Systems', icon: '⚙️' },
  { value: 'companyWide', label: 'Company-wide Tasks', icon: '📋' },
  { value: 'branchServices', label: 'Branch Services', icon: '🏢' },
];

const STATUS_OPTIONS = ['Not Started', 'In Progress', 'On Hold', 'Completed'].map((s) => ({ value: s, label: s }));

function normalizeIcon(value) {
  if (!value) return null;
  const v = String(value).trim();
  return v.length ? v : null;
}

export default function TaskTemplatesManager() {
  const queryClient = useQueryClient();
  const [sectors, setSectors] = useState([]);
  const [sectorId, setSectorId] = useState(null);
  const [group, setGroup] = useState('internalSystems');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const formSchema = useMemo(() => taskTemplateSchema, []);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sectorId: 0,
      group: 'internalSystems',
      title: '',
      code: '',
      defaultStatus: 'Not Started',
      icon: '',
      sortOrder: 0,
      isRequired: false,
      isActive: true,
      fieldsSchema: null,
    },
  });
  const watchedDefaultStatus = watch('defaultStatus');

  async function fetchSectors() {
    try {
      const res = await api.get('/admin/sectors');
      const list = res.data || [];
      setSectors(list);
      const def = list.find((s) => s.isDefault) || list[0];
      setSectorId(def?.id ?? null);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load sectors');
    }
  }

  async function fetchTemplates(nextSectorId, nextGroup, bustCache = false) {
    if (!nextSectorId) return;
    setLoading(true);
    try {
      const params = { sectorId: nextSectorId, group: nextGroup, includeInactive: true };
      // Add cache-busting timestamp when explicitly requested (after delete operations)
      if (bustCache) {
        params._t = Date.now();
      }
      const res = await api.get('/admin/task-templates', { params });
      setRows(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSectors();
  }, []);

  useEffect(() => {
    if (sectorId) fetchTemplates(sectorId, group);
  }, [sectorId, group]);

  function openCreate() {
    if (!sectorId) return;
    setEditing(null);
    reset({
      sectorId,
      group,
      title: '',
      code: '',
      defaultStatus: 'Not Started',
      icon: '',
      sortOrder: rows.length ? Math.max(...rows.map((r) => Number(r.sortOrder) || 0)) + 10 : 10,
      isRequired: false,
      isActive: true,
      fieldsSchema: null,
    });
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    reset({
      sectorId: row.sectorId,
      group: row.group,
      title: row.title || '',
      code: row.code || '',
      defaultStatus: row.defaultStatus || 'Not Started',
      icon: row.icon || '',
      sortOrder: Number(row.sortOrder) || 0,
      isRequired: !!row.isRequired,
      isActive: !!row.isActive,
      fieldsSchema: row.fieldsSchema ?? null,
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      ...values,
      sectorId: Number(values.sectorId),
      sortOrder: Number(values.sortOrder) || 0,
      icon: normalizeIcon(values.icon),
    };
    try {
      if (editing?.id) {
        await api.put(`/admin/task-templates/${editing.id}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/admin/task-templates', payload);
        toast.success('Template created');
      }
      setModalOpen(false);
      // Invalidate React Query cache for all task templates
      queryClient.invalidateQueries({ queryKey: ['taskTemplates'] });
      await fetchTemplates(sectorId, group, true); // Bust cache after save/update
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to save template');
    }
  }

  async function disableRow(row) {
    if (!window.confirm(`Disable template "${row.title}"?\n\nThis will hide it from new selections but keep existing tasks.`)) return;
    try {
      await api.delete(`/admin/task-templates/${row.id}`);
      toast.success('Template disabled');
      // Invalidate React Query cache for all task templates
      queryClient.invalidateQueries({ queryKey: ['taskTemplates'] });
      await fetchTemplates(sectorId, group, true); // Bust cache after deletion
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to disable template');
    }
  }

  async function deleteRow(row) {
    if (!window.confirm(`⚠️ PERMANENTLY DELETE template "${row.title}"?\n\nThis action CANNOT be undone!\n\nAll references to this template will be removed.`)) return;
    if (!window.confirm(`Are you absolutely sure you want to PERMANENTLY delete "${row.title}"?\n\nClick OK to confirm permanent deletion.`)) return;
    try {
      await api.delete(`/admin/task-templates/${row.id}?hard=true`);
      toast.success('Template permanently deleted');
      // Invalidate React Query cache for all task templates
      queryClient.invalidateQueries({ queryKey: ['taskTemplates'] });
      await fetchTemplates(sectorId, group, true); // Bust cache after deletion
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to delete template');
    }
  }

  const sectorOptions = sectors.map((s) => ({ value: String(s.id), label: `${s.name}${s.isDefault ? ' (Default)' : ''}` }));
  const groupMeta = GROUPS.find((g) => g.value === group);

  return (
    <ModernCard>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Templates</h2>
          <p className="text-gray-600 dark:text-slate-400 text-sm">Manage internal systems, company tasks, and branch services</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="min-w-[260px]">
            <label className="block text-sm text-gray-700 dark:text-slate-300 mb-1">Sector</label>
            <select
              value={sectorId ?? ''}
              onChange={(e) => setSectorId(Number(e.target.value))}
              className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white"
            >
              {sectorOptions.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-white dark:bg-slate-800">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <ModernButton variant="primary" onClick={openCreate} disabled={!sectorId}>
            + Add Template
          </ModernButton>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 mb-4">
        {GROUPS.map((g) => (
          <button
            key={g.value}
            onClick={() => setGroup(g.value)}
            className={`px-4 py-3 rounded-2xl font-semibold transition-all border flex-shrink-0 ${
              group === g.value
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-emerald-500/20 shadow-lg shadow-emerald-500/20'
                : 'bg-white dark:bg-white/5 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-white/10 border-gray-200 dark:border-white/10'
            }`}
          >
            <span className="mr-2">{g.icon}</span>
            {g.label}
          </button>
        ))}
      </div>

      <div className="text-gray-700 dark:text-slate-300 text-sm mb-3">
        Showing: <span className="text-gray-900 dark:text-white font-semibold">{groupMeta?.label}</span>
      </div>

      {loading ? (
        <div className="text-gray-700 dark:text-slate-300">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-gray-700 dark:text-slate-300">No templates found for this sector/group.</div>
      ) : (
        <>
          <div className="md:hidden space-y-3">
            {rows.map((r) => {
              const iconObj = r.icon && solidIcons[r.icon] ? solidIcons[r.icon] : null;
              const displayIcon = iconObj ? (
                <FontAwesomeIcon icon={iconObj} />
              ) : r.icon ? (
                <span>{r.icon}</span>
              ) : '—';

              return (
                <div key={r.id} className="border border-gray-200 dark:border-white/10 rounded-xl p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-gray-900 dark:text-white font-semibold">
                        {displayIcon} {r.title}
                        {r.isRequired ? <span className="text-xs text-amber-600 dark:text-amber-300 ml-2">(required)</span> : null}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-slate-400">{r.code}</div>
                    </div>
                    <ModernBadge variant={r.isActive ? 'success' : 'secondary'}>
                      {r.isActive ? 'Active' : 'Disabled'}
                    </ModernBadge>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-slate-400">
                    Default: <span className="text-gray-900 dark:text-white">{r.defaultStatus}</span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-slate-400">
                    Order: <span className="text-gray-900 dark:text-white">{r.sortOrder}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <ModernButton variant="glass" onClick={() => openEdit(r)}>
                      Edit
                    </ModernButton>
                    <ModernButton variant="danger" onClick={() => disableRow(r)} disabled={!r.isActive}>
                      Disable
                    </ModernButton>
                    <ModernButton
                      variant="danger"
                      onClick={() => deleteRow(r)}
                      className="bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500"
                    >
                      Delete
                    </ModernButton>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
            <thead>
              <tr className="text-gray-600 dark:text-slate-400 text-sm">
                <th className="py-2">Icon</th>
                <th className="py-2">Title</th>
                <th className="py-2">Code</th>
                <th className="py-2">Default Status</th>
                <th className="py-2">Order</th>
                <th className="py-2">Active</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // Try Font Awesome first, fallback to emoji/text
                const iconObj = r.icon && solidIcons[r.icon] ? solidIcons[r.icon] : null;
                const displayIcon = iconObj ? (
                  <FontAwesomeIcon icon={iconObj} />
                ) : r.icon ? (
                  <span>{r.icon}</span>
                ) : '—';
                
                return (
                  <tr key={r.id} className="border-t border-gray-200 dark:border-white/10">
                    <td className="py-3 text-gray-900 dark:text-white text-xl">
                      {displayIcon}
                    </td>
                    <td className="py-3 text-gray-900 dark:text-white font-medium">
                      {r.title}{' '}
                      {r.isRequired ? <span className="text-xs text-amber-600 dark:text-amber-300 ml-2">(required)</span> : null}
                    </td>
                    <td className="py-3 text-gray-700 dark:text-slate-300">{r.code}</td>
                    <td className="py-3">
                      <ModernBadge variant="secondary">{r.defaultStatus}</ModernBadge>
                    </td>
                    <td className="py-3 text-gray-700 dark:text-slate-300">{r.sortOrder}</td>
                    <td className="py-3">
                      <ModernBadge variant={r.isActive ? 'success' : 'secondary'}>{r.isActive ? 'Yes' : 'No'}</ModernBadge>
                    </td>
                    <td className="py-3 text-right space-x-2">
                      <ModernButton variant="glass" onClick={() => openEdit(r)}>
                        Edit
                      </ModernButton>
                      <ModernButton variant="danger" onClick={() => disableRow(r)} disabled={!r.isActive}>
                        Disable
                      </ModernButton>
                      <ModernButton 
                        variant="danger" 
                        onClick={() => deleteRow(r)}
                        className="bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500"
                      >
                        Delete
                      </ModernButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Template' : 'Add Template'}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <input type="hidden" {...register('sectorId', { valueAsNumber: true })} />
          <input type="hidden" {...register('group')} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ModernInput
              label="Title"
              placeholder="What users see"
              error={errors.title?.message}
              {...register('title')}
            />
            <ModernInput
              label="Code"
              placeholder="stable-key (lowercase)"
              error={errors.code?.message}
              {...register('code')}
            />
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Icon (optional)
              </label>
              <Controller
                name="icon"
                control={control}
                render={({ field }) => (
                  <IconPicker
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.icon?.message && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.icon.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Sort Order
              </label>
              <Controller
                name="sortOrder"
                control={control}
                render={({ field }) => (
                  <input
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? 0 : Number(e.target.value))}
                    onBlur={field.onBlur}
                    className="w-full px-4 py-2.5 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  />
                )}
              />
              {errors.sortOrder?.message && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.sortOrder.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Default Status</label>
              <select
                value={watchedDefaultStatus || 'Not Started'}
                {...register('defaultStatus')}
                className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.defaultStatus?.message && (
                <div className="text-xs text-red-600 dark:text-red-300 mt-1">{errors.defaultStatus.message}</div>
              )}
            </div>
            <div className="flex items-center gap-6 pt-7">
              <label className="flex items-center gap-2 text-gray-700 dark:text-slate-200 text-sm font-medium">
                <input type="checkbox" {...register('isActive')} className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-white/10 rounded focus:ring-blue-500" />
                Active
              </label>
              <label className="flex items-center gap-2 text-gray-700 dark:text-slate-200 text-sm font-medium">
                <input type="checkbox" {...register('isRequired')} className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-white/10 rounded focus:ring-blue-500" />
                Required
              </label>
            </div>
          </div>

          <div className="text-xs text-gray-600 dark:text-slate-400 font-medium">
            Group: <span className="text-gray-900 dark:text-slate-200 font-semibold">{groupMeta?.label}</span> • Sector ID: <span className="text-gray-900 dark:text-slate-200 font-semibold">{sectorId}</span>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <ModernButton variant="glass" type="button" onClick={() => setModalOpen(false)}>
              Cancel
            </ModernButton>
            <ModernButton variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save'}
            </ModernButton>
          </div>
        </form>
      </Modal>
    </ModernCard>
  );
}


